<template>
  <!-- With something to say on hover, the address is a popover trigger. With
       nothing (no geo database, or an address the database does not know) it is
       plain text: a button that opens a panel repeating the address the reader is
       already looking at is a control that does nothing. It also stops truncating
       in that state, because the popover was the only way to see the rest. -->
  <IPInfoPopover
    v-if="hasHoverDetail"
    :ip="ip"
    :ip-info="resolvedIpInfo"
    :loading="resolvedLoading"
    :not-found="resolvedNotFound"
  >
    <span class="inline-flex items-center gap-1">
      <span v-if="showFlags && (!collapseFlag || flag)" class="inline-flex w-5 justify-center text-base leading-none">{{
        flag
      }}</span>
      <span :class="textClass"><HighlightText :text="ip" :query="highlight" /></span>
    </span>
  </IPInfoPopover>

  <!-- No geo panel, but an IPv6 literal is up to 39 characters, so `break-all` wrapped it over three
       lines and doubled the row height. Shortened, with the whole value in a popover. -->
  <ValuePopover v-else-if="needsValuePopover" :value="ip">
    <!-- The marker sits on the slot content, not on ValuePopover: HoverPopover has two roots, so a
         fallthrough attribute on it is dropped. -->
    <span class="inline-flex items-center gap-1" data-testid="ip-value-popover">
      <span v-if="showFlags && (!collapseFlag || flag)" class="inline-flex w-5 justify-center text-base leading-none">{{
        flag
      }}</span>
      <span :class="textClass"><HighlightText :text="ip" :query="highlight" /></span>
    </span>
  </ValuePopover>

  <span v-else class="inline-flex items-center gap-1" data-testid="ip-plain">
    <span v-if="showFlags && (!collapseFlag || flag)" class="inline-flex w-5 justify-center text-base leading-none">{{
      flag
    }}</span>
    <span :class="textClass"><HighlightText :text="ip" :query="highlight" /></span>
  </span>
</template>

<script setup lang="ts">
import { computed, shallowRef, watch } from "vue";
import HighlightText from "./ui/HighlightText.vue";
import IPInfoPopover from "./ui/IPInfoPopover.vue";
import ValuePopover from "./ui/ValuePopover.vue";
import { useIPInfo, ipInfoAvailable, type IPInfoData, type UseIPInfoReturn } from "../composables/useIPInfo";
import { countryCodeToFlag, PLACEHOLDER_FLAG, NOT_FOUND_FLAG } from "../utils/countryCodeToFlag";

const props = withDefaults(
  defineProps<{
    ip: string;
    ipInfo?: IPInfoData | null;
    truncate?: boolean;
    hideFlag?: boolean;
    collapseFlag?: boolean;
    /** Prose variant, for an IP inside a sentence rather than a table cell. */
    linkStyle?: boolean;
    /** Search term to mark, where the list this address sits in has a search box. */
    highlight?: string;
  }>(),
  {
    ipInfo: undefined,
    truncate: true,
    hideFlag: false,
    collapseFlag: false,
    linkStyle: false,
    highlight: "",
  },
);

// When ipInfo prop is provided, skip the client-side lookup entirely.
const hasProvidedIpInfo = computed(() => props.ipInfo !== undefined);

// Holds the current useIPInfo return. Only used when no ipInfo prop is provided.
const source = shallowRef<UseIPInfoReturn | null>(hasProvidedIpInfo.value ? null : useIPInfo(props.ip));

watch(
  () => props.ip,
  (newIp) => {
    source.value = hasProvidedIpInfo.value ? null : useIPInfo(newIp);
  },
);

// Resolved values: use prop data when provided, otherwise use composable data.
const resolvedLoading = computed(() => (hasProvidedIpInfo.value ? false : (source.value?.loading.value ?? false)));
const resolvedIpInfo = computed(() =>
  hasProvidedIpInfo.value ? (props.ipInfo ?? null) : (source.value?.data.value ?? null),
);
const resolvedNotFound = computed(() => !resolvedLoading.value && !resolvedIpInfo.value);

/** Show flags when the DB is available, or when provided ipInfo has a country code */
const showFlags = computed(() => {
  if (props.hideFlag) return false;
  if (hasProvidedIpInfo.value) {
    return !!props.ipInfo?.country_code;
  }
  return ipInfoAvailable.value === true;
});

// Whether the popover would show anything the reader cannot already see. Loading
// counts: the answer is on its way. Everything else falls back to reprinting the
// address, which is what makes the trigger pointless.
const hasHoverDetail = computed(() => {
  if (resolvedLoading.value) return true;
  const info = resolvedIpInfo.value;
  if (!info) return false;
  return Boolean(info.is_private || info.country || info.country_code || info.asn || info.as_name || info.as_domain);
});

// Only an IPv6 literal can outrun a table cell, so the fallback popover is gated on the address
// being one. `linkStyle` is prose, where the address wraps inside a sentence.
const needsValuePopover = computed(
  () => props.truncate && !props.linkStyle && !hasHoverDetail.value && props.ip.includes(":"),
);

// Truncating is only safe while something can reveal the rest: the geo popover, or the value
// popover above.
const canTruncate = computed(() => hasHoverDetail.value || needsValuePopover.value);

const textClass = computed(() => [
  "font-mono",
  props.linkStyle
    ? "break-all text-inherit underline decoration-dotted decoration-1 underline-offset-2"
    : ["text-xs text-foreground/70", props.truncate && canTruncate.value ? "block max-w-[7rem] truncate" : "break-all"],
]);

const flag = computed(() => {
  if (resolvedLoading.value) {
    return PLACEHOLDER_FLAG;
  }
  if (resolvedIpInfo.value?.is_private) {
    return PLACEHOLDER_FLAG;
  }
  if (resolvedIpInfo.value?.country_code) {
    return countryCodeToFlag(resolvedIpInfo.value.country_code);
  }
  // Public IP not found in geo DB
  if (resolvedIpInfo.value && !resolvedIpInfo.value.country_code) {
    return NOT_FOUND_FLAG;
  }
  return PLACEHOLDER_FLAG;
});
</script>
